// pages/posts/[slug].js
import ErrorPage from "next/error";
import { NextSeo } from "next-seo";
import { useRouter } from "next/router";
import { groq } from "next-sanity";
import { usePreviewSubscription, urlFor } from "../../../lib/sanity";
import { getClient } from "../../../lib/sanity.server";
import Header from "../../../view/components/Header/Header";
import Hero from "../../../view/components/Hero/Hero";
import Newsletter from "../../../view/components/Footer/components/Newsletter/Newsletter";
import Footer from "../../../view/components/Footer/Footer";
import { trimText } from "../../../common/helper";
import Content from "../../../view/screens/Blog/components/Content/Content";
import Sidebar from "../../../view/screens/Blog/components/Sidebar/Sidebar";

const authorQuery = groq`
  *[_type == "author" && slug.current == $slug][0] {
    _id,
    name,
    title,
    description,
    "slug": slug.current
  }
`;

const postsQuery = groq`
*[_type == "post" && !(_id in path("drafts.**")) &&  author->slug.current == $slug  ] {
    _id,
    title,
    publishedAt,
    body,
    mainImage,
    author ->{
      name,
      image,
      title,
      slug
    },
    categories[]->{
      _id,
      title,
      slug
    },
    slug
  }
`;

export default function AuthorPage({ data, preview }) {
    const router = useRouter();

    if (!router.isFallback && !data.author?.slug) {
        return <ErrorPage statusCode={404} />;
    }

    const { _id, name, title, description, slug } = data.author;
    const { posts } = data;

    return (
        <div>
            <Header />
            <NextSeo
                title={`Dzobs.com Blog | ${name}`}
                description={
                    description ? trimText(description, 160) : `Stranica autora`
                }
                canonical={`https://www.dzobs.com/blog/autor/${slug}`}
            />
            <Hero title={name} subtitle={description ? description : ``} />

            <div className="max-w-screen-xl mx-auto px-6 xl:px-0 ">
                <div className="mb-8 leading-loose">
                    <section className="flex flex-col md:flex-row md:space-x-16 body-font overflow-hidden">
                        <div className="md:w-2/3">
                            <Content posts={posts} isHome={false} />
                        </div>
                        <Sidebar />
                    </section>
                </div>
            </div>
            <Newsletter bg="bg-gray-light" />
            <Footer />
        </div>
    );
}

export async function getStaticProps({ params, preview = false }) {
    const author = await getClient(preview).fetch(authorQuery, {
        slug: params.slug,
    });

    const posts = await getClient(preview).fetch(postsQuery, {
        slug: params.slug,
    });

    return {
        props: {
            preview,
            data: { author, posts },
        },
        revalidate: 10,
    };
}

export async function getStaticPaths() {
    const paths = await getClient().fetch(
        groq`*[_type == "author" && defined(slug.current)][].slug.current`
    );

    return {
        paths: paths.map((slug) => ({ params: { slug } })),
        fallback: "blocking",
    };
}
